// engine2025/params/state.ts — volcado de parámetros estatales P-001..P-196.
//
// Diseño (fijado en F0.T0.3): en vez de un volcado único de los 196 al principio (violaría
// "contexto mínimo" — exigiría leer ~20 capítulos no citados por F0), cada fase añade aquí
// SOLO los parámetros de su propio bloque, leyendo la §4 del capítulo que esa fase cita.
// `ParamRegistry.get` lanza si un id no se ha registrado todavía, así que un bloque que
// intente usar un parámetro de una fase futura falla alto y explícito, no en silencio.
//
// Registrado hasta ahora:
//   F0 (§A.1 Dependent, Cap.14):        P-001, P-002
//   F1 (§A.2 EmploymentIncome, Cap.02+Cap.03): P-010..P-012, P-020, P-023, P-030..P-035, P-NEW-06-11
//   F2 (§A.3 RealEstate, Cap.04):       P-040..P-042, P-044..P-048, P-NEW-07-1, P-NEW-07-2
//   F3 (§A.4 MovableCapital, Cap.05):   P-050, P-051-MULTIPLE, P-052, P-NEW-08-5
//   F4 (§A.5 EconomicActivity, Cap.06-09): P-060, P-070..P-073, P-NEW-10-1, P-NEW-11-2/3/14,
//                                          P-EPIGRAFE-673.1, P-AGRI-PRODUCT-INDEX, P-NEW-12-11
//   F5 (§A.6 SpecialRegimes, Cap.10):   P-049 (los tipos 2%/1,1% reutilizan P-NEW-07-1/07-2 de F2)
//   F6 (§A.7 CapitalGains, Cap.11):     P-091, P-092, P-NEW-14-2/3/4, P-NEW-14-14/15
//   F7 (§A.8 IncomeIntegration, Cap.12): P-100, P-101
//   F8 (§A.9 BaseReductions, Cap.13):   P-110..P-122, P-NEW-16-2, P-NEW-16-3
//   F9 (§A.10 Minimums, Cap.14):        P-130..P-143, P-NEW-17-1..8 (vía params/minimums.ts)
//   F10 (§A.11 GrossQuota, Cap.15):     P-150, P-151-XX, P-152..P-154, P-NEW-18-1/2 (vía params/scales.ts)
//   F11 (§A.12 StateDeductions, Cap.16): P-160..P-175 (los del caso-16), P-NEW-19-2/7
//   F12 (§A.13 RegionalDeductions):     P-RI-BIRTH, P-RI-FOSTER (catálogos en params/regional/*.ts)
//   F13 (§A.14 FinalSettlement, Cap.18): P-180..P-184, P-NEW-21-1/2/5
//   F14 (§A.15 Filing, Cap.01):         P-185, P-190..P-196, P-NEW-04-2

import { ParamRegistry } from './registry';
import { parseEuros } from '../money';
import { registerMinimumParams } from './minimums';
import { registerScaleParams } from './scales';

/** Construye el registro de parámetros estatales 2025. Se inyecta en cada `blocks/*.ts`. */
export function buildStateParamRegistry(): ParamRegistry {
    const params = new ParamRegistry();

    // --- Bloque 9 — Mínimo personal y familiar (_schema.md §A.10, entidad Dependent §A.1) ---
    // Fuente: docs/guia-renta-2025/14-minimo-personal-familiar.md §4 (Cap.17 del manual).
    params.define('P-001', parseEuros('8.000,00'), {
        concept: 'Límite renta anual del descendiente/ascendiente (excluidas exentas) para dar derecho al mínimo',
        verified: true,
        source: { chapter: 'Cap.14 — 14-minimo-personal-familiar.md', page: 'p. 1072 / 1076' },
    });
    params.define('P-002', parseEuros('1.800,00'), {
        concept: 'Límite renta del descendiente/ascendiente que presenta declaración propia',
        verified: true,
        source: { chapter: 'Cap.14 — 14-minimo-personal-familiar.md', page: 'p. 1072 / 1077' },
    });

    // --- Bloque 1 — Rendimientos del trabajo (F1, _schema.md §A.2) ---
    // Fuente: docs/guia-renta-2025/03-rendimientos-trabajo.md §4 (Cap.03), salvo donde se indica.
    params.define('P-010', 30, {
        concept: 'Reducción rendimientos irregulares/>2 años (Art. 18.2 Ley IRPF), en % sobre la base',
        verified: true,
        source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 231-232' },
    });
    params.define('P-011', parseEuros('300.000,00'), {
        concept: 'Base máxima anual conjunta de la reducción del 30% (Art. 18.2)',
        verified: true,
        source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 235' },
    });
    params.define(
        'P-012',
        { lowerBound: parseEuros('700.000,00'), upperBound: parseEuros('1.000.000,00') },
        {
            concept:
                'Taper de la reducción 30% en extinción de relación laboral/mercantil: ' +
                'base = 300.000 − (indemnización − 700.000) entre estos dos límites; 0 € por encima del superior',
            verified: true,
            source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 236' },
        }
    );
    params.define('P-020', parseEuros('180.000,00'), {
        concept: 'Tope absoluto de exención de la indemnización por despido/cese (Art. 7.e)',
        verified: true,
        source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 251' },
    });
    params.define('P-023', parseEuros('60.100,00'), {
        concept: 'Exención trabajos efectivamente realizados en el extranjero (Art. 7.p)',
        verified: true,
        source: { chapter: 'Cap.02 — 02-cuestiones-generales.md', page: 'p. 102-105' },
    });
    params.define('P-030', parseEuros('500,00'), {
        concept: 'Tope cuotas colegios profesionales de colegiación obligatoria (Art. 19.2.d)',
        verified: true,
        source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 253' },
    });
    params.define('P-031', parseEuros('300,00'), {
        concept: 'Tope gastos de defensa jurídica en litigio con el pagador (Art. 19.2.e)',
        verified: true,
        source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 253' },
    });
    params.define('P-032', parseEuros('2.000,00'), {
        concept: 'Gasto genérico "otros gastos" (Art. 19.2.f)',
        verified: true,
        source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 254' },
    });
    params.define(
        'P-033',
        { d33_64: parseEuros('3.500,00'), d65OrMobility: parseEuros('7.750,00') },
        {
            concept: 'Incremento trabajador activo con discapacidad (33-64% / ≥65% o movilidad reducida-ayuda de terceros)',
            verified: true,
            source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 256' },
        }
    );
    params.define(
        'P-034',
        { upperThreshold: parseEuros('19.747,50'), otherIncomeLimit: parseEuros('6.500,00') },
        {
            concept:
                'Umbral RNT de la reducción art. 20 (0 € de reducción a partir de aquí) + límite de ' +
                '"otras rentas" (excl. exentas) para tener derecho a la reducción',
            verified: true,
            source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 258-259 (umbral); p. 258 (otras rentas, cita en §3)' },
        }
    );
    params.define(
        'P-035',
        { maxReductionThreshold: parseEuros('14.852,00'), maxReduction: parseEuros('7.302,00') },
        {
            concept: 'RNT por debajo del cual se aplica la reducción máxima art. 20 sin tramo, y su importe',
            verified: true,
            source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 258' },
        }
    );
    params.define(
        'P-035-TRAMOS',
        // Tabla completa por tramos del art. 20 (ex-GAP F1 de _discrepancias.md): el manual
        // pp. 258-259 imprime la fórmula íntegra; la extracción a .md solo capturó los extremos.
        {
            firstBracketUpper: parseEuros('17.673,52'),
            firstBracketSlope: 1.75,
            secondBracketBase: parseEuros('2.364,34'),
            secondBracketSlope: 1.14,
        },
        {
            concept:
                'Reducción art. 20 — tramos intermedios: 14.852<RNT≤17.673,52 → 7.302 − 1,75×(RNT−14.852); ' +
                '17.673,52<RNT<19.747,5 → 2.364,34 − 1,14×(RNT−17.673,52)',
            verified: true,
            source: { chapter: 'Cap.03 — docs/_raw/cap-03.txt líneas 4097-4120 (PDF Parte 1)', page: 'pp. 258-259' },
        }
    );
    params.define('P-NEW-06-11', parseEuros('2.000,00'), {
        concept: 'Incremento gasto deducible por movilidad geográfica (año del cambio + siguiente)',
        verified: true,
        source: { chapter: 'Cap.03 — 03-rendimientos-trabajo.md', page: 'p. 254-255' },
    });

    // --- Bloque 2 — Capital inmobiliario + imputación (F2, _schema.md §A.3) ---
    // Fuente: docs/guia-renta-2025/04-rendimientos-capital-inmobiliario.md §4 (Cap.04, único
    // capítulo — ya cubre rendimientos, tope de gastos e imputación/parentesco).
    params.define('P-040', 3, {
        concept: 'Amortización inmueble, % anual sobre el mayor de (coste construcción excl. suelo, VC construcción)',
        verified: true,
        source: { chapter: 'Cap.04 — 04-rendimientos-capital-inmobiliario.md', page: 'p. 277-278' },
    });
    params.define('P-041', 10, {
        concept: 'Amortización mobiliario cedido con el inmueble, % anual (tabla simplificada Orden 27-03-1998)',
        verified: true,
        source: { chapter: 'Cap.04 — 04-rendimientos-capital-inmobiliario.md', page: 'p. 280' },
    });
    params.define('P-042', { years: 4 }, {
        concept:
            'Arrastre del exceso de intereses+conservación sobre el límite (= ingresos íntegros del ' +
            'inmueble): años durante los que se puede deducir el exceso',
        verified: true,
        source: { chapter: 'Cap.04 — 04-rendimientos-capital-inmobiliario.md', page: 'p. 273-275' },
    });
    params.define('P-044', 90, {
        concept: 'Reducción alquiler vivienda — zona tensionada + rebaja renta >5% vs. contrato anterior',
        verified: true,
        source: { chapter: 'Cap.04 — 04-rendimientos-capital-inmobiliario.md', page: 'p. 283-284' },
    });
    params.define('P-045', 70, {
        concept: 'Reducción alquiler vivienda — 1er alquiler zona tensionada a 18-35 años, o AAPP/entidad sin fines lucrativos',
        verified: true,
        source: { chapter: 'Cap.04 — 04-rendimientos-capital-inmobiliario.md', page: 'p. 284' },
    });
    params.define('P-046', 60, {
        concept: 'Reducción alquiler vivienda — rehabilitación finalizada ≤2 años antes del contrato',
        verified: true,
        source: { chapter: 'Cap.04 — 04-rendimientos-capital-inmobiliario.md', page: 'p. 285' },
    });
    params.define('P-047', 50, {
        concept: 'Reducción alquiler vivienda — general, resto de contratos desde 26-05-2023',
        verified: true,
        source: { chapter: 'Cap.04 — 04-rendimientos-capital-inmobiliario.md', page: 'p. 285' },
    });
    params.define('P-048', 60, {
        concept: 'Reducción alquiler vivienda — régimen transitorio, contratos anteriores a 26-05-2023 (DT 38ª)',
        verified: true,
        source: { chapter: 'Cap.04 — 04-rendimientos-capital-inmobiliario.md', page: 'p. 285-287' },
    });
    params.define('P-NEW-07-1', 2, {
        concept: 'Imputación de rentas / rendimiento mínimo parentesco — % general sobre valor catastral',
        verified: true,
        source: { chapter: 'Cap.04 — 04-rendimientos-capital-inmobiliario.md', page: 'p. 288-289' },
    });
    params.define('P-NEW-07-2', 1.1, {
        concept: 'Imputación de rentas / rendimiento mínimo parentesco — % si VC revisado desde 1-1-2012',
        verified: true,
        source: { chapter: 'Cap.04 — 04-rendimientos-capital-inmobiliario.md', page: 'p. 288-289' },
    });

    // --- Bloque 3 — Capital mobiliario (F3, _schema.md §A.4) ---
    // Fuente: docs/guia-renta-2025/05-rendimientos-capital-mobiliario.md §4 (Cap.05).
    params.define('P-052', 19, {
        concept: 'Retención general RCM (dividendos, intereses, cesión a terceros, seguros, otros Art.25.4)',
        verified: true,
        source: { chapter: 'Cap.05 — 05-rendimientos-capital-mobiliario.md', page: 'p. 317-324' },
    });
    params.define('P-051-MULTIPLE', 3, {
        concept: 'Regla de vinculadas (Art. 46.a): múltiplo de fondos propios de la entidad sobre el que el exceso cedido va a base general',
        verified: true,
        source: { chapter: 'Cap.05 — 05-rendimientos-capital-mobiliario.md', page: 'p. 326-327' },
    });
    params.define(
        'P-NEW-08-5',
        { pct: 30, maxBase: parseEuros('300.000,00') },
        {
            concept: 'Reducción 30% rendimientos irregulares Art.25.4 (OTHER_ART25_4): % y base máxima anual',
            verified: true,
            source: { chapter: 'Cap.05 — 05-rendimientos-capital-mobiliario.md', page: 'p. 356' },
        }
    );
    params.define(
        'P-050',
        {
            lifeAnnuityByAge: { '<40': 40, '40-49': 35, '50-59': 28, '60-65': 24, '66-69': 20, '>=70': 8 },
            temporaryAnnuityByDuration: { '<=5': 12, '5-10': 16, '10-15': 20, '>15': 25 },
        },
        {
            concept: 'Rentas vitalicias/temporales inmediatas (Art. 25.3): % de la anualidad según edad/duración',
            verified: true,
            source: { chapter: 'Cap.05 — 05-rendimientos-capital-mobiliario.md', page: 'p. 336, 342-343 (vitalicias); p. 337, 343 (temporales)' },
        }
    );

    // --- Bloque 4 — Actividades económicas (F4, _schema.md §A.5) ---
    // Fuente: docs/guia-renta-2025/06..09-*.md (Cap.06-09), salvo donde se indica.
    params.define('P-060', parseEuros('600.000,00'), {
        concept: 'Umbral cifra de negocios EDN vs EDS (año anterior)',
        verified: true,
        source: { chapter: 'Cap.06/07 — 06-actividades-economicas-cuestiones-generales.md / 07-metodo-estimacion-directa.md', page: 'p. 387 / p. 404-405' },
    });
    params.define('P-070', parseEuros('250.000,00'), {
        concept: 'Umbral ingresos íntegros AE (excl. agrícola/ganadera/forestal) para exclusión de EO',
        verified: true,
        source: { chapter: 'Cap.06 — 06-actividades-economicas-cuestiones-generales.md', page: 'p. 387, 391' },
    });
    params.define('P-071', parseEuros('125.000,00'), {
        concept: 'Umbral ingresos con factura obligatoria a empresarios para exclusión de EO',
        verified: true,
        source: { chapter: 'Cap.06 — 06-actividades-economicas-cuestiones-generales.md', page: 'p. 387, 391' },
    });
    params.define('P-072', parseEuros('250.000,00'), {
        concept: 'Umbral volumen de compras (excl. inmovilizado) para exclusión de EO',
        verified: true,
        source: { chapter: 'Cap.06 — 06-actividades-economicas-cuestiones-generales.md', page: 'p. 387, 391' },
    });
    params.define('P-073', 5, {
        concept: 'Reducción general rendimiento neto de módulos EO (no agrícola y agrícola, mismo % en 2025)',
        verified: true,
        source: { chapter: 'Cap.08 — 08-estimacion-objetiva-no-agricola.md / Cap.09', page: 'p. 538-539 / p. 609' },
    });
    params.define(
        'P-NEW-10-1',
        { pct: 5, maxAmount: parseEuros('2.000,00') },
        {
            concept: 'Gastos de difícil justificación EDS: % s/diferencia positiva y tope anual por contribuyente',
            verified: true,
            source: { chapter: 'Cap.07 — 07-metodo-estimacion-directa.md', page: 'p. 447-448' },
        }
    );
    params.define('P-NEW-11-2', 0.4, {
        concept: 'EO no agrícola — coeficiente por incremento de personal asalariado (× diferencia positiva 2025 vs 2024)',
        verified: true,
        source: { chapter: 'Cap.08 — 08-estimacion-objetiva-no-agricola.md', page: 'p. 522-523' },
    });
    params.define(
        'P-NEW-11-3',
        {
            brackets: [
                { upTo: 1.0, rate: 0.1 },
                { upTo: 3.0, rate: 0.15 },
                { upTo: 5.0, rate: 0.2 },
                { upTo: 8.0, rate: 0.25 },
                { upTo: null, rate: 0.3 },
            ],
        },
        {
            concept: 'EO no agrícola — coeficiente por tramos del módulo "personal asalariado" (marginal, sobre unidades tras el incremento)',
            verified: true,
            source: { chapter: 'Cap.08 — 08-estimacion-objetiva-no-agricola.md', page: 'p. 523-524' },
        }
    );
    params.define('P-NEW-11-14', { multiplier: 1.3 }, {
        concept: 'EO no agrícola — índice corrector de exceso sobre la cuantía umbral de cada actividad',
        verified: true,
        source: { chapter: 'Cap.08 — 08-estimacion-objetiva-no-agricola.md', page: 'p. 532-536' },
    });
    params.define(
        'P-EPIGRAFE-673.1',
        {
            excessThreshold: parseEuros('30.586,03'),
            modules: {
                PERSONAL_ASALARIADO: parseEuros('4.056,30'),
                PERSONAL_NO_ASALARIADO: parseEuros('15.538,66'),
                POTENCIA_ELECTRICA: parseEuros('321,23'),
                MESAS: parseEuros('233,04'),
                LONGITUD_BARRA: parseEuros('371,62'),
                MAQUINAS_A: parseEuros('957,39'),
                MAQUINAS_B: parseEuros('2.903,66'),
            },
        },
        {
            concept:
                'Epígrafe IAE 673.1 (cafés y bares de categoría especial): rendimiento anual por unidad de módulo y ' +
                'cuantía del índice de exceso. Cap.08 §4 declina reproducir estas ~63 tablas por volumen; cifras ' +
                'tomadas directamente de tests/golden/caso-08.md, que cita Apéndice del manual p. 546',
            verified: true,
            source: { chapter: 'tests/golden/caso-08.md (cita Manual_práctico_de_Renta_2025._Parte_1..pdf)', page: 'Apéndice p. 546' },
        }
    );
    params.define(
        'P-AGRI-PRODUCT-INDEX',
        // Ex-divergencia F4 de _discrepancias.md, RESUELTA: el 0,09 del código 3 (ovino de carne)
        // procede de la Orden HAC/484/2026 (BOE 18-05-2026), que rebaja para 2025 los índices de
        // sectores afectados por circunstancias excepcionales — cuadro "Explotaciones ganaderas"
        // del Apéndice del manual (pp. 619-620), filas con (*): ovino/caprino carne 0,09, leche 0,18.
        // El 0,13 de Cap.09 §4 es el índice base de la Orden HAC/1347/2024 sin esa rebaja.
        { byProductCode: { '3': 0.09, '6': 0.26, '15': 0.56 } },
        {
            concept: 'EO agrícola — índice de rendimiento neto por código de producto/servicio (solo códigos 3/6/15, los de caso-09; código 3 = ovino de carne con rebaja Orden HAC/484/2026)',
            verified: true,
            source: { chapter: 'Apéndice Cap.09 — docs/_raw/cap-09.txt líneas 1888-1943 (PDF Parte 1)', page: 'pp. 619-620' },
        }
    );
    params.define(
        'P-NEW-12-11',
        {
            brackets: [
                { minPct: 10, maxPct: 20, index: 0.9 },
                { minPct: 20, maxPct: 30, index: 0.85 },
                { minPct: 30, maxPct: 40, index: 0.8 },
                { minPct: 40, maxPct: null, index: 0.75 },
            ],
        },
        {
            concept: 'EO agrícola — índice corrector "personal asalariado" por tramos de coste/ingresos totales',
            verified: true,
            source: { chapter: 'Cap.09 — 09-actividades-economicas-estimacion-objetiva-agricola.md', page: 'p. 606' },
        }
    );

    // --- Bloque 5 — Regímenes especiales (F5, _schema.md §A.6) ---

    params.define(
        'P-049',
        { ratePct: 1.1, basePct: 50 },
        {
            concept:
                'Imputación inmobiliaria sin valor catastral: 1,1% × 50% × mayor(valor adquisición, ' +
                'valor comprobado por la Administración a efectos de otros tributos)',
            verified: true,
            source: { chapter: 'Cap.10 — 10-regimenes-especiales-imputacion-atribucion-rentas.md', page: 'p. 803' },
        }
    );

    // --- Bloque 6 — Ganancias y pérdidas patrimoniales (F6, _schema.md §A.7) ---

    params.define('P-091', parseEuros('400.000,00'), {
        concept:
            'Límite conjunto vitalicio (individual) del valor de transmisión con derecho a coeficientes ' +
            'de abatimiento DT 9ª, acumulado desde 1-1-2015',
        verified: true,
        source: { chapter: 'Cap.11 — 11-ganancias-perdidas-patrimoniales.md', page: 'p. 868' },
    });
    params.define('P-092', parseEuros('240.000,00'), {
        concept: 'Límite de reinversión en renta vitalicia para la exención de mayores de 65 años (art. 38.3)',
        verified: true,
        source: { chapter: 'Cap.11 — 11-ganancias-perdidas-patrimoniales.md', page: 'p. 962' },
    });
    params.define('P-NEW-14-2', 25, {
        concept: 'Coeficiente de abatimiento DT 9ª — acciones cotizadas (% por año de permanencia >2 a 31-12-1996)',
        verified: true,
        source: { chapter: 'Cap.11 — 11-ganancias-perdidas-patrimoniales.md', page: 'p. 875' },
    });
    params.define('P-NEW-14-3', 11.11, {
        concept: 'Coeficiente de abatimiento DT 9ª — inmuebles y derechos reales sobre inmuebles',
        verified: true,
        source: { chapter: 'Cap.11 — 11-ganancias-perdidas-patrimoniales.md', page: 'p. 875' },
    });
    params.define('P-NEW-14-4', 14.28, {
        concept: 'Coeficiente de abatimiento DT 9ª — resto de bienes y derechos (incl. FI/SICAV, no cotizadas)',
        verified: true,
        source: { chapter: 'Cap.11 — 11-ganancias-perdidas-patrimoniales.md', page: 'p. 875' },
    });
    params.define(
        'P-NEW-14-14',
        { months: 2 },
        {
            concept: 'Plazo de recompra de valores homogéneos cotizados que difiere la pérdida (wash sale, ±2 meses)',
            verified: true,
            source: { chapter: 'Cap.11 — 11-ganancias-perdidas-patrimoniales.md', page: 'p. 857' },
        }
    );
    params.define(
        'P-NEW-14-15',
        { months: 12 },
        {
            concept: 'Plazo de recompra de valores homogéneos no cotizados/otros elementos que difiere la pérdida (±1 año)',
            verified: true,
            source: { chapter: 'Cap.11 — 11-ganancias-perdidas-patrimoniales.md', page: 'p. 857' },
        }
    );

    // --- Bloque 7 — Integración y compensación (F7, _schema.md §A.8) ---

    params.define('P-100', 25, {
        concept:
            'Límite de compensación del saldo negativo de GyP no-transmisión (BI general) contra el saldo ' +
            'positivo de rendimientos e imputaciones: % sobre dicho saldo ANTES de compensaciones, cupo ' +
            'CONJUNTO entre el ejercicio propio y los pendientes 2021-2024',
        verified: true,
        source: { chapter: 'Cap.12 — 12-integracion-compensacion-rentas.md', page: 'p. 1000-1001' },
    });
    params.define('P-101', 25, {
        concept:
            'Límite de compensación cruzada RCM ahorro ↔ GyP por transmisión (BI ahorro): % sobre el saldo ' +
            'positivo del componente compensado ANTES de compensaciones, cupo conjunto propio+pendientes',
        verified: true,
        source: { chapter: 'Cap.12 — 12-integracion-compensacion-rentas.md', page: 'p. 1003-1005' },
    });

    // --- Bloque 8 — Reducciones de la base imponible (F8, _schema.md §A.9) ---

    params.define('P-110', parseEuros('3.400,00'), {
        concept: 'Reducción por tributación conjunta — unidad familiar biparental',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1013' },
    });
    params.define('P-111', parseEuros('2.150,00'), {
        concept: 'Reducción por tributación conjunta — unidad familiar monoparental',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1013' },
    });
    params.define('P-112', 30, {
        concept: 'Límite porcentual de reducción por SPS: % sobre RNT + RNAE del ejercicio',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1026-1027, 1034' },
    });
    params.define('P-113', parseEuros('1.500,00'), {
        concept: 'Límite general anual de aportaciones a sistemas de previsión social',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1023, 1026' },
    });
    params.define('P-114', parseEuros('8.500,00'), {
        concept: 'Incremento del límite por contribuciones empresariales + aportación equiparada del trabajador (máx. conjunto con P-116)',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1023-1024, 1027' },
    });
    params.define(
        'P-115',
        {
            rntThreshold: parseEuros('60.000,00'),
            lowBand: parseEuros('500,00'),
            midBand: parseEuros('1.500,00'),
            lowMultiplier: 2.5,
            midFixed: parseEuros('1.250,00'),
            midMarginalRate: 0.25,
        },
        {
            concept:
                'Tabla de aportación máxima del trabajador al mismo plan de empleo según RNT del empleador ' +
                'y contribución empresarial (CE≤500: ×2,5; 500<CE≤1.500: 1.250+0,25×(CE−500); resto: ×1)',
            verified: true,
            source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1023-1024, 1027-1028' },
        }
    );
    params.define('P-116', parseEuros('4.250,00'), {
        concept: 'Incremento por aportaciones propias de autónomos a planes de empleo sectoriales/simplificados',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1024-1025, 1028' },
    });
    params.define('P-117', parseEuros('8.000,00'), {
        concept: 'Umbral de RNT+RNAE del cónyuge para la reducción por aportaciones al cónyuge',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1040' },
    });
    params.define('P-118', parseEuros('1.000,00'), {
        concept: 'Reducción máxima anual por aportaciones a SPS del cónyuge',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1040' },
    });
    params.define('P-119', parseEuros('10.000,00'), {
        concept: 'Límite anual de aportación de cada pariente/cónyuge/tutor a SPS de persona con discapacidad',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1043-1044' },
    });
    params.define('P-120', parseEuros('24.250,00'), {
        // Cap.13 §4 CORRIGE _schema.md: el valor 24.750 € anotado allí para la aportación propia
        // no consta en el manual; 24.250 € rige tanto para la propia como para el conjunto.
        concept:
            'SPS discapacidad: límite de la aportación de la propia persona con discapacidad Y límite ' +
            'conjunto de todos los aportantes (mismo importe)',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1043-1045' },
    });
    params.define(
        'P-121',
        { perContributor: parseEuros('10.000,00'), joint: parseEuros('24.250,00') },
        {
            concept: 'Patrimonio protegido: límite por aportante y límite conjunto de todos los aportantes',
            verified: true,
            source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1048-1049' },
        }
    );
    params.define('P-122', parseEuros('24.250,00'), {
        concept: 'Mutualidad de deportistas profesionales: aportación máxima anual y límite de reducción',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1054-1056' },
    });
    params.define('P-NEW-16-1', parseEuros('5.000,00'), {
        concept:
            'Límite propio e independiente para primas de la empresa a seguros colectivos de dependencia ' +
            '(se suma a 1.500 + incrementos en la fórmula del Cap.13 §3.2 paso 2)',
        verified: true,
        source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1025-1026, 1028-1029' },
    });
    params.define(
        'P-NEW-16-2',
        { years: 5 },
        {
            concept: 'Plazo de traslado de excesos no reducidos (SPS general, discapacidad, deportistas)',
            verified: true,
            source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1030-1032, 1046' },
        }
    );
    params.define(
        'P-NEW-16-3',
        { years: 4 },
        {
            concept: 'Plazo de traslado de excesos no reducidos (patrimonios protegidos — distinto del de SPS)',
            verified: true,
            source: { chapter: 'Cap.13 — 13-reducciones-base-imponible-general.md', page: 'p. 1050-1051' },
        }
    );

    // --- Bloque 9 — Mínimo personal y familiar (F9, _schema.md §A.10) ---
    registerMinimumParams(params);

    // --- Bloque 10 — Cuota íntegra: escalas 2025 (F10, _schema.md §A.11) ---
    registerScaleParams(params);

    // --- Bloque 11 — Deducciones estatales de la cuota (F11, _schema.md §A.12) ---

    params.define(
        'P-160',
        { statePct: 7.5, regionalPct: 7.5, maxBase: parseEuros('9.040,00') },
        {
            concept: 'Vivienda habitual transitoria (DT 18ª): % por tramo y base máxima anual (adquisición/rehabilitación)',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1169-1171' },
        }
    );
    params.define(
        'P-NEW-19-2',
        { statePct: 10, regionalPct: 10, maxBase: parseEuros('12.080,00') },
        {
            concept: 'Vivienda habitual transitoria: obras de adecuación por discapacidad (% por tramo y base máxima independiente)',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1172-1173' },
        }
    );
    params.define('P-162', 50, {
        concept: 'Inversión en empresas de nueva o reciente creación: % de deducción (exclusivamente estatal)',
        verified: true,
        source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1174' },
    });
    params.define('P-163', parseEuros('100.000,00'), {
        concept: 'Inversión en empresas de nueva o reciente creación: base máxima anual',
        verified: true,
        source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1174-1175' },
    });
    params.define(
        'P-164',
        { amount: parseEuros('250,00'), pct: 80, prioritizedPct: 85 },
        {
            concept: 'Donativos Ley 49/2002: primer tramo (250 €) al 80% (85% mecenazgo prioritario)',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1281-1282' },
        }
    );
    params.define(
        'P-166',
        { pct: 40, prioritizedPct: 45 },
        {
            concept: 'Donativos Ley 49/2002: resto de la base al 40% (45% mecenazgo prioritario)',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1281-1282' },
        }
    );
    params.define(
        'P-NEW-19-7',
        { pct: 45, prioritizedPct: 50 },
        {
            concept: 'Donativos Ley 49/2002: tipo incrementado por reiteración 3 años (45%; 50% prioritario)',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1281' },
        }
    );
    params.define(
        'P-167',
        { generalPct: 10, prioritizedPct: 15 },
        {
            concept: 'Límite conjunto de base de donativos sobre la base liquidable (10%; 15% mecenazgo prioritario)',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1289' },
        }
    );
    params.define(
        'P-168',
        { pct: 20, maxBase: parseEuros('600,00') },
        {
            concept: 'Aportaciones a partidos políticos: % y base máxima anual',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1289' },
        }
    );
    params.define('P-169', 60, {
        concept: 'Deducción por rentas obtenidas en Ceuta o Melilla: % y límite máximo sobre cuota',
        verified: true,
        source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1289-1290, 1296' },
    });
    params.define(
        'P-171',
        { pct: 20, maxBase: parseEuros('5.000,00') },
        {
            concept: 'Obras de mejora: reducción demanda calefacción/refrigeración (−7%): % y base máxima anual',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1307-1308' },
        }
    );
    params.define(
        'P-172',
        { pct: 40, maxBase: parseEuros('7.500,00') },
        {
            concept: 'Obras de mejora: reducción consumo energía primaria no renovable (−30% o clase A/B)',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1315-1316' },
        }
    );
    params.define(
        'P-173',
        { pct: 60, maxBase: parseEuros('5.000,00'), cumulativeMaxBase: parseEuros('15.000,00') },
        {
            concept: 'Obras de rehabilitación energética de edificios: %, base máxima anual y acumulado 4 años',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1322-1323' },
        }
    );
    params.define(
        'P-174',
        { pct: 15, maxBase: parseEuros('20.000,00') },
        {
            concept: 'Adquisición de vehículo eléctrico enchufable/pila de combustible: % y base máxima',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1332-1333' },
        }
    );
    params.define(
        'P-175',
        { pct: 15, maxBase: parseEuros('4.000,00') },
        {
            concept: 'Instalación de infraestructura de recarga: % y base máxima',
            verified: true,
            source: { chapter: 'Cap.16 — 16-deducciones-generales-cuota.md', page: 'p. 1335-1336' },
        }
    );

    // --- Bloque 12 — Deducciones autonómicas: evaluadores especiales La Rioja (F12) ---

    params.define(
        'P-RI-BIRTH',
        {
            first: parseEuros('600,00'),
            second: parseEuros('750,00'),
            thirdPlus: parseEuros('900,00'),
            multipleExtra: parseEuros('60,00'),
        },
        {
            concept: 'La Rioja — nacimiento/adopción: importes por nº de orden POR PROGENITOR (+60 € parto múltiple)',
            verified: true,
            source: { chapter: 'Cap.32 — 32-deducciones-la-rioja.md (P-NEW-40-3..6)', page: 'p. 482' },
        }
    );
    params.define(
        'P-RI-FOSTER',
        {
            fullAmount: parseEuros('300,00'),
            fullThresholdDays: 183,
            halfAmount: parseEuros('150,00'),
            sumThresholdDays: 90,
        },
        {
            concept:
                'La Rioja — acogimiento de menores: 300 € si ≥183 días, 150 € si >90 y <183; los ' +
                'acogimientos ≤90 días se suman entre sí',
            verified: true,
            source: { chapter: 'Cap.32 — 32-deducciones-la-rioja.md (P-NEW-40-12..15)', page: 'p. 487-488' },
        }
    );

    // --- Bloque 13 — Cuota líquida, diferencial y resultado (F13, _schema.md §A.14) ---

    params.define(
        'P-180',
        { annual: parseEuros('1.200,00'), monthly: parseEuros('100,00'), bonus: parseEuros('150,00') },
        {
            concept: 'Deducción por maternidad: 1.200 €/año por hijo <3 años (100 €/mes) + 150 € el mes de los 30 días cotizados',
            verified: true,
            source: { chapter: 'Cap.18 — 18-cuota-liquida-diferencial-resultado.md', page: 'p. 1382-1383' },
        }
    );
    params.define('P-181', parseEuros('1.000,00'), {
        concept: 'Incremento por gastos de custodia en guardería/centro infantil autorizado (límite anual)',
        verified: true,
        source: { chapter: 'Cap.18 — 18-cuota-liquida-diferencial-resultado.md', page: 'p. 1388' },
    });
    params.define(
        'P-182',
        { general: parseEuros('1.200,00'), special: parseEuros('2.400,00'), perExtraChild: parseEuros('600,00') },
        {
            concept: 'Deducción por familia numerosa: general/especial + incremento por hijo que excede el mínimo de la categoría',
            verified: true,
            source: { chapter: 'Cap.18 — 18-cuota-liquida-diferencial-resultado.md', page: 'p. 1397-1398' },
        }
    );
    params.define('P-183', parseEuros('1.200,00'), {
        concept: 'Deducción por descendiente/ascendiente con discapacidad a cargo (cada uno)',
        verified: true,
        source: { chapter: 'Cap.18 — 18-cuota-liquida-diferencial-resultado.md', page: 'p. 1397' },
    });
    params.define('P-184', parseEuros('1.200,00'), {
        // Cap.18 §4 CORRIGE _schema.md (que consignaba 1.500 €): el manual fija 1.200 €/año.
        concept: 'Deducción por cónyuge no separado legalmente con discapacidad a cargo',
        verified: true,
        source: { chapter: 'Cap.18 — 18-cuota-liquida-diferencial-resultado.md', page: 'p. 1397' },
    });
    params.define('P-NEW-21-1', parseEuros('1.200,00'), {
        concept: 'Deducción por ascendiente separado/sin vínculo con dos hijos sin anualidades por alimentos',
        verified: true,
        source: { chapter: 'Cap.18 — 18-cuota-liquida-diferencial-resultado.md', page: 'p. 1397-1398' },
    });
    params.define(
        'P-NEW-21-2',
        {
            accessThreshold: parseEuros('18.276,00'),
            fixedUpTo: parseEuros('16.576,00'),
            amount: parseEuros('340,00'),
            taperRate: 0.2,
            otherIncomeMax: parseEuros('6.500,00'),
        },
        {
            concept:
                'Deducción por obtención de rendimientos del trabajo (DA 61ª, novedad 2025): 340 € si RIT < 16.576; ' +
                'decreciente 0,2×exceso hasta 18.276; otras rentas ≤ 6.500 €',
            verified: true,
            source: { chapter: 'Cap.18 — 18-cuota-liquida-diferencial-resultado.md', page: 'p. 1370-1371' },
        }
    );
    // G1-5: en 2026 el 4,0625% NO procede de una LPGE 2026 sino de la prórroga presupuestaria
    // (LPGE 2023 prorrogada); la AEAT lo declara vigente de forma provisional "hasta que se
    // apruebe y entre en vigor la LPGE para 2026" (Manual Renta 2025, Cap. 18, reglas de cálculo).
    params.define('P-NEW-21-5', 4.0625, {
        concept: 'Tipo de interés de demora vigente 2023-2026 (regularización por pérdida de deducciones)',
        verified: true,
        source: { chapter: 'Cap.18 — 18-cuota-liquida-diferencial-resultado.md', page: 'p. 1360-1363' },
    });

    // --- Bloque 14 — Obligación de declarar y gestión (F14, _schema.md §A.15) ---

    const filingSrc = (page: string) => ({
        chapter: 'Cap.01 — 01-campana-declaracion-renta-2025.md',
        page,
    });
    params.define('P-190', parseEuros('22.000,00'), {
        concept: 'Obligación de declarar — RT con un solo pagador (o 2º y restantes ≤ 1.500 €)',
        verified: true,
        source: filingSrc('p. 25'),
    });
    params.define('P-191', parseEuros('15.876,00'), {
        concept: 'Obligación de declarar — RT con varios pagadores (2º+ > 1.500 €) y supuestos b)-d) del art. 96.3',
        verified: true,
        source: filingSrc('p. 25-26'),
    });
    params.define('P-192', parseEuros('1.500,00'), {
        concept: 'Obligación de declarar — umbral del 2º y restantes pagadores',
        verified: true,
        source: filingSrc('p. 25'),
    });
    params.define('P-193', parseEuros('1.600,00'), {
        concept: 'Obligación de declarar — RCM + GPP sujetos a retención (límite conjunto)',
        verified: true,
        source: filingSrc('p. 27'),
    });
    params.define('P-194', parseEuros('1.000,00'), {
        concept: 'Obligación de declarar — rentas imputadas + Letras del Tesoro + subvenciones VPO + GPP de ayudas públicas',
        verified: true,
        source: filingSrc('p. 27'),
    });
    params.define('P-195', parseEuros('500,00'), {
        concept: 'Obligación de declarar — regla 2ª: pérdidas patrimoniales (deben ser < 500 € para exonerar)',
        verified: true,
        source: filingSrc('p. 30'),
    });
    params.define('P-NEW-04-2', parseEuros('1.000,00'), {
        concept: 'Obligación de declarar — regla 2ª: umbral combinado RT+RCM/RCI+RNAE+GPP',
        verified: true,
        source: filingSrc('p. 30'),
    });
    params.define(
        'P-196',
        { from: '2026-04-08', to: '2026-06-30', directDebitUntil: '2026-06-25' },
        {
            concept: 'Campaña Renta 2025: ventana de presentación y fin de domiciliación bancaria',
            verified: true,
            source: filingSrc('p. 35, 38, 50-51'),
        }
    );
    params.define(
        'P-185',
        { firstPct: 60, secondPct: 40, secondDueDate: '2026-11-05' },
        {
            concept: 'Fraccionamiento del resultado a ingresar sin interés: 60% al presentar / 40% hasta 05-11-2026',
            verified: true,
            source: filingSrc('p. 49-52'),
        }
    );

    return params;
}
